<script setup lang="ts">
import { useI18n } from '#i18n';

// validation
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';

// components
import SvgIcon from '~/components/SvgIcon.vue';
import AppInput from '~/components/redesign/AppInput.vue';
import Buttons from '~/components/redesign/Buttons';

// constants
import { REGEX } from '~/constants';

// utils
import { isObject, isString } from '~/utils';

export type SubmittedData = Record<'email' | 'fullname', string>;

const emit = defineEmits<{
  submitted: [data: SubmittedData];
}>();

const { t } = useI18n();
const { $pp } = useNuxtApp();

// https://github.com/jquense/yup#localization-and-i18n
yup.setLocale({
  mixed: {
    required: 'Validations.This_field_is_required',
  },
  string: {
    max: ({ max }) => ({ key: 'Validations.max', values: { max } }),
    matches: 'Validations.Invalid_email_address',
  },
});

const localizeErrorMessage = (
  validationError:
    | undefined
    | string
    | { key: string; values: { max: number } },
): string | undefined => {
  let validationMessage: string | undefined;

  if (isString(validationError)) {
    validationMessage = t(validationError);
  }

  if (isObject(validationError)) {
    const { key, values } = validationError;

    validationMessage = t(key, { length: values.max });
  }

  return validationMessage;
};

const validationSchema = toTypedSchema(
  yup.object({
    fullname: yup
      .string()
      .trim()
      .max(128)
      .required()
      .default($pp?.get('fullname')),

    email: yup
      .string()
      .trim()
      .max(128)
      .required()
      .matches(REGEX.EMAIL)
      .companyEmail('Validations.Invalid_corporate_email')
      .email()
      .default($pp?.get('email')),
  }),
);

const { errors, defineField, handleSubmit, meta } = useForm({
  validationSchema,
});

const [fullname] = defineField('fullname');
const [email] = defineField('email');

const submitHandler = handleSubmit(values => {
  emit('submitted', values as SubmittedData);
});
</script>

<template>
  <form data-component-name="Forms.Chatlio" @submit="submitHandler">
    <div class="greetings">
      <SvgIcon name="logo-without-label" />
      <strong>{{ $t('Forms.Chatlio.greetings.strong') }}</strong>
    </div>

    <div class="form-inputs">
      <AppInput
        v-model="fullname"
        :label="$t('Full_Name')"
        :error="localizeErrorMessage(errors.fullname)"
        required
        size="large"
        name="fullname"
      />

      <AppInput
        v-model="email"
        :label="$t('Corporate_Email')"
        :error="localizeErrorMessage(errors.email)"
        required
        size="large"
        type="email"
        name="email"
      />

      <Buttons.Regular :disabled="!meta.valid" size="large">
        {{ $t('Forms.Chatlio.form-inputs.Buttons.Regular') }}
        <SvgIcon name="arrow-forward" />
      </Buttons.Regular>
    </div>
  </form>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/typo';

[data-component-name='Forms.Chatlio'] {
  .greetings {
    @include flex.center-start;

    flex-direction: column;
    gap: 1rem 0;
    margin-bottom: 2rem;

    svg {
      @include size.fixed(3.125rem);
    }

    strong {
      text-align: center;

      @include typo.subtitle-3;
    }
  }

  .form-inputs {
    @include flex.stretch-start;

    flex-direction: column;
    gap: 0.875rem 0;
  }
}
</style>
